import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Provider, useSelector, useDispatch } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ReduxStore from "./redux/store";
import { fetchEbayStatusRequest } from "./redux/ebay/ebay.actions";
import GlobalStyles from "./GlobalStyles";
import { LoadingProvider } from "./context/LoadingContext";
import { BatchUploadProgressProvider } from "./context/BatchUploadProgressContext";
import { PopupStackProvider } from "./context/PopupStackContext";
import BatchUploadProgressPopup from "./components/common/BatchUploadProgressPopup";
import {
  LOGIN,
  REGISTER,
  SIGNUP,
  SIGNUP_PROCESS,
  DASHBOARD,
  UPLOAD,
  EBAY,
  EBAY_SYNC,
  EBAY_SYNC_DETAIL,
  BATCHES,
  GLOBAL_PRESETS,
  TITLE_BUILDER,
  POLICIES,
  PROFILE_SETTINGS,
  PAYMENT_DETAILS,
  AUTH_SUCCESS,
  FLOWMAIL_DASHBOARD,
  TEST_PAGE,
} from "./routes/routes";

import AuthPage from "./components/auth/AuthPage";
import Register from "./components/register/Register";
import SignupProcess from "./components/signup-process/SignupProcess";
import Layout from "./components/layout/Layout";
import Dashboard from "./components/dashboard/Dashboard";
import CardsView from "./components/dashboard/CardsView";
import Upload from "./components/upload/Upload";
import Ebay from "./components/ebay/Ebay";
import EbaySyncTasks from "./components/ebay-sync/EbaySyncTasks";
import EbaySyncTaskDetail from "./components/ebay-sync/EbaySyncTaskDetail";
import Batches from "./components/batches/Batches";
import BatchCards from "./components/batches/BatchCards";
import GlobalPresetsList from "./components/global-presets/GlobalPresetsList";
import GlobalPresetsForm from "./components/global-presets/GlobalPresetsForm";
import TitleBuilder from "./components/title-builder/TitleBuilder";
import Policies from "./components/policies/Policies";
import ProfileSettings from "./components/profile-settings/ProfileSettings";
import PaymentDetails from "./components/payment-details/PaymentDetails";
import AuthSuccess from "./components/auth-success/AuthSuccess";
import FlowMailDashboard from "./components/flowmail-dashboard/FlowMailDashboard";
import TestPage from "./components/test-page/TestPage";
import "./App.css";

function AuthenticatedLayout() {
  return (
    <Layout>
      <Routes>
        <Route path={DASHBOARD} element={<Dashboard />} />
        <Route path={BATCHES} element={<Batches />} />
        <Route path={`${BATCHES}/:batchName`} element={<BatchCards />} />
        <Route path="/batches/:id/cards" element={<CardsView />} />
        <Route
          path={UPLOAD}
          element={
            <Upload
              onUploadComplete={() => (window.location.href = DASHBOARD)}
            />
          }
        />
        <Route path={EBAY} element={<Ebay />} />
        <Route path={EBAY_SYNC} element={<EbaySyncTasks />} />
        <Route path={EBAY_SYNC_DETAIL} element={<EbaySyncTaskDetail />} />
        <Route path={GLOBAL_PRESETS} element={<GlobalPresetsList />} />
        <Route path={`${GLOBAL_PRESETS}/new`} element={<GlobalPresetsForm />} />
        <Route path={`${GLOBAL_PRESETS}/edit/:id`} element={<GlobalPresetsForm />} />
        <Route path={TITLE_BUILDER} element={<TitleBuilder />} />
        <Route path={POLICIES} element={<Policies />} />
        <Route path={PROFILE_SETTINGS} element={<ProfileSettings />} />
        <Route path={PAYMENT_DETAILS} element={<PaymentDetails />} />
        <Route path={TEST_PAGE} element={<TestPage />} />
        <Route path="*" element={<Navigate to={DASHBOARD} replace />} />
      </Routes>
    </Layout>
  );
}

function AppContent() {
  const dispatch = useDispatch();
  const { token, admin_data } = useSelector((state) => state.auth);
  const hasFetchedEbayStatusOnLoad = useRef(false);

  useEffect(() => {
    if (!token || !admin_data) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: admin_data?.id ? String(admin_data.id) : undefined,
      email: admin_data?.email,
      username: admin_data?.username,
    });
  }, [token, admin_data]);

  // Check eBay status once on app load/reload (login itself triggers its
  // own check in the sign-in saga, so this only needs to run on mount).
  // Guarded by a ref since StrictMode double-invokes effects in development.
  useEffect(() => {
    if (token && !hasFetchedEbayStatusOnLoad.current) {
      hasFetchedEbayStatusOnLoad.current = true;
      dispatch(fetchEbayStatusRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth success page is standalone - no layout needed
  if (!token) {
    return (
      <Routes>
        <Route path={AUTH_SUCCESS} element={<AuthSuccess />} />
        <Route path={FLOWMAIL_DASHBOARD} element={<FlowMailDashboard />} />
        <Route path={LOGIN} element={<AuthPage />} />
        <Route path={REGISTER} element={<Register />} />
        <Route path={SIGNUP} element={<AuthPage />} />
        <Route path={SIGNUP_PROCESS} element={<SignupProcess />} />
        <Route path="*" element={<Navigate to={LOGIN} replace />} />
      </Routes>
    );
  }

  // For authenticated users, check if it's auth-success first
  return (
    <Routes>
      <Route path={AUTH_SUCCESS} element={<AuthSuccess />} />
      <Route path={FLOWMAIL_DASHBOARD} element={<FlowMailDashboard />} />
      <Route path="*" element={<AuthenticatedLayout />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={ReduxStore.store}>
      <PersistGate loading={null} persistor={ReduxStore.persistor}>
        <LoadingProvider>
          <GlobalStyles />
          <Router>
            <PopupStackProvider>
              <BatchUploadProgressProvider>
                <AppContent />
                <BatchUploadProgressPopup />
                <ToastContainer />
              </BatchUploadProgressProvider>
            </PopupStackProvider>
          </Router>
        </LoadingProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
